import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Library as LibraryIcon, Loader2, X, Plus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Library() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: "", author: "", isbn: "", category: "", copies: 1 });

  useEffect(() => { fetchBooks(); }, []);

  const fetchBooks = async () => {
    try {
      const { data } = await axios.get(`${API}/library/books`, { withCredentials: true });
      setBooks(data);
    } catch {} finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/library/books`, { ...form, copies: parseInt(form.copies) }, { withCredentials: true });
      setShowModal(false); fetchBooks();
    } catch {}
  };

  const totalBooks = books.reduce((s, b) => s + (b.copies || 0), 0);
  const available = books.reduce((s, b) => s + (b.available || 0), 0);
  const categories = [...new Set(books.map(b => b.category))];

  return (
    <div className="space-y-6" data-testid="library-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Operations</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Library Management</h2>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="add-book-btn"
          className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Add Book
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Books", value: totalBooks, color: "#0A7AFF" },
          { label: "Available", value: available, color: "#22C55E" },
          { label: "Issued", value: totalBooks - available, color: "#F59E0B" },
          { label: "Categories", value: categories.length, color: "#8B5CF6" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white border border-[#E2DDD4] p-5" style={{ borderLeft: `3px solid ${s.color}` }}>
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-2">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-3xl text-[#0C0C10]">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="bg-white border border-[#E2DDD4]">
        <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "2fr 1.5fr 1fr 80px 80px 80px" }}>
          {["Title", "Author", "Category", "ISBN", "Copies", "Available"].map(h => (
            <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
          ))}
        </div>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
          : books.map((b, i) => (
          <div key={b._id} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "2fr 1.5fr 1fr 80px 80px 80px" }}>
            <div>
              <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{b.title}</p>
            </div>
            <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{b.author}</span>
            <span className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider px-2 py-1 border border-[#E2DDD4] text-[#7A7468] w-fit">{b.category}</span>
            <span className="font-['JetBrains_Mono'] text-[10px] text-[#BDBAA8]">{b.isbn}</span>
            <span className="font-['JetBrains_Mono'] text-xs font-bold text-[#0C0C10]">{b.copies}</span>
            <span className={`font-['JetBrains_Mono'] text-xs font-bold ${b.available > 0 ? "text-[#22C55E]" : "text-[#FF3636]"}`}>{b.available}</span>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6"><h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Add Book</h3><button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button></div>
              <form onSubmit={handleAdd} className="space-y-4">
                {[["title", "Title"], ["author", "Author"], ["isbn", "ISBN"], ["category", "Category"], ["copies", "Number of Copies"]].map(([f, l]) => (
                  <div key={f}><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-1.5">{l}</label>
                  <input type={f === "copies" ? "number" : "text"} value={form[f]} onChange={e => setForm(p => ({...p, [f]: e.target.value}))} required
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" /></div>
                ))}
                <button type="submit" className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors mt-2">Add Book</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
